/**
 * Звук оверлея: один AudioContext на страницу.
 *
 * Отдельный модуль, потому что здесь встречаются две неприятности
 * браузера, и обе решаются не в виджете.
 *
 * ПЕРВАЯ: AudioContext стартует в состоянии `suspended`, пока
 * на странице не было действия пользователя. В Browser Source
 * действий не бывает ВООБЩЕ — там нет ни мыши, ни клавиатуры. Значит,
 * ждать жеста бессмысленно: контекст надо будить самим, и делать это
 * при первом же признаке жизни (docs/04).
 *
 * ВТОРАЯ: Web Audio есть не везде, где выполняется этот код.
 * В тестовом DOM его нет вовсе, и падать из-за этого оверлей
 * не должен: без звука он остаётся рабочим оверлеем.
 */

import {
  FULL_AUDIO,
  loudnessGain,
  type AirConfig,
  type AirFrame,
  type ChannelAudio,
  type SoundPlay,
} from '@owm/scene';
import { setAudioGraph, SoundPlayer, WebAudioGraph } from '@owm/widgets';

import { createAirPlayer, type AirDecoderFactory } from './air.js';

/** Что умеет звук оверлея снаружи. */
export interface OverlayAudio {
  /**
   * Будит контекст, если тот заснул.
   *
   * Зовётся на каждом сообщении от сервера, а не один раз: браузер
   * усыпляет контекст и сам — например, когда вкладку сворачивают.
   * Повторный вызов на бодром контексте ничего не стоит.
   */
  resume(): void;
  /**
   * Ставит уровень и потолок канала.
   *
   * Потолок прижимает уровень и здесь — последним рубежом: сервер
   * это уже проверил, но «ни через какой путь» из постановки задачи
   * означает в том числе сообщение, собранное мимо панели.
   */
  apply(levels: ChannelAudio): void;
  /**
   * Дёргает или отпускает стоп-кран по звуку.
   *
   * ПЕРЕВЕШИВАЕТ УРОВЕНЬ КАНАЛА, пока включён: сообщение о громкости
   * от кого-то с правом `audio:master` не должно вернуть звук
   * в эфир посреди аварии. Отпускается — и уровень возвращается тот,
   * что был, а не полный: стоп-кран перекрывает звук, а не
   * настраивает его.
   */
  silence(on: boolean): void;
  /**
   * Играет звук саундборда.
   *
   * Идёт МИМО приглушения, прямо в мастер: пусти его тем же путём,
   * что и фон, — и он приглушал бы сам себя (docs/04).
   */
  play(sound: SoundPlay): void;
  /**
   * Затыкает один запущенный звук.
   *
   * Не стоп-кран: тот глушит ВЕСЬ звук эфира, включая музыку
   * и ролики, и снимается отдельным действием. Здесь кончается
   * одно нажатие.
   */
  stopSound(triggerId: string): void;
  /**
   * Эфир своим звуком: модератор начал вещать.
   *
   * Поток идёт ТУДА ЖЕ, куда саундборд, — прямо в мастер. Значит
   * потолок канала и стоп-кран накрывают его сами собой, без
   * отдельной ветки, про которую однажды забудут.
   */
  airOn(config: AirConfig): void;
  /** Кадр эфира. */
  airFrame(frame: AirFrame): void;
  /** Эфир снят. */
  airOff(): void;
  /**
   * Контекст, которым играют.
   *
   * Нужен прогреву: буфер, декодированный ЧУЖИМ контекстом,
   * в этом не зазвучит — частоты дискретизации у них разные.
   * null — Web Audio недоступен, прогревать звуки нечем.
   */
  readonly context: AudioContext | null;
  /** Есть ли звук вообще. */
  readonly available: boolean;
}

/**
 * Создаёт граф и объявляет его виджетам.
 *
 * Возвращает управление разбуженностью: сам граф виджеты берут
 * из @owm/widgets, а будить его оттуда нечем — там нет ни сокета,
 * ни понятия «пришло сообщение».
 */
export function startAudio(decoder?: AirDecoderFactory): OverlayAudio {
  const Context = audioContextClass();

  if (Context === null) {
    // Браузер без Web Audio. Слои со звуком заглушат себя сами:
    // им вернётся отсутствие графа
    return {
      resume: () => {},
      apply: () => {},
      silence: () => {},
      play: () => {},
      stopSound: () => {},
      airOn: () => {},
      airFrame: () => {},
      airOff: () => {},
      context: null,
      available: false,
    };
  }

  const context = new Context();
  const graph = new WebAudioGraph(context);
  setAudioGraph(graph);

  const player = new SoundPlayer({ graph: () => graph });

  // Эфир включается в мастер, а не в узел приглушения: приглушение
  // существует ради саундборда, и поток, идущий через него, глушил
  // бы сам себя на каждой кнопке
  const air = createAirPlayer({
    context,
    destination: graph.master,
    ...(decoder ? { createDecoder: decoder } : {}),
  });

  // Последние уровни канала и состояние стоп-крана держатся здесь
  // вместе: они спорят за одно и то же значение, и решать спор
  // должно одно место
  let levels: ChannelAudio = FULL_AUDIO;
  let silenced = false;

  const applyLevels = (): void => {
    // СТОП-КРАН ПЕРЕВЕШИВАЕТ УРОВЕНЬ КАНАЛА. Без этого сообщение
    // о громкости, пришедшее во время аварии, вернуло бы звук
    // в эфир — а прислать его может кто угодно с правом
    // audio:master, не подозревая, что владелец только что
    // дёрнул стоп-кран
    graph.setMaster(silenced ? 0 : levels.master, levels.ceiling);
  };

  return {
    apply(next: ChannelAudio): void {
      levels = next;
      applyLevels();
    },
    silence(on: boolean): void {
      silenced = on;
      applyLevels();
    },
    play(sound: SoundPlay): void {
      player.play({
        // Нажатие, а не кнопка: одну и ту же кнопку жмут дважды
        // подряд, и остановить надо конкретный звук
        id: sound.triggerId,
        url: sound.url,
        gain: sound.gain,
        // Выравнивание считается ЗДЕСЬ, а не на сервере: измеренное
        // значение лежит в базе как есть, чтобы можно было передумать
        // и поменять цель, не трогая файлы (4.1)
        loudnessGain: loudnessGain(sound.loudness),
        durationMs: sound.durationMs,
      });
    },
    stopSound(triggerId: string): void {
      player.stop(triggerId);
    },
    airOn(config: AirConfig): void {
      air.start(config);
    },
    airFrame(frame: AirFrame): void {
      air.push(frame);
    },
    airOff(): void {
      air.stop();
    },
    resume(): void {
      if (context.state !== 'suspended') return;

      // Отказ игнорируется: браузер вправе не разбудить контекст
      // без жеста, и настаивать бессмысленно. В Browser Source
      // это разрешено, в обычной вкладке — нет, и оверлей обязан
      // работать в обоих
      void context.resume().catch(() => {});
    },
    context,
    available: true,
  };
}

/**
 * Достаёт конструктор контекста, если он есть.
 *
 * webkitAudioContext — для старых сборок CEF: OBS обновляют
 * не все и не сразу, а звук нужен именно там.
 */
function audioContextClass(): typeof AudioContext | null {
  const scope = globalThis as unknown as {
    AudioContext?: typeof AudioContext;
    webkitAudioContext?: typeof AudioContext;
  };

  return scope.AudioContext ?? scope.webkitAudioContext ?? null;
}
